import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiStar, FiCalendar, FiClock, FiCheck, FiUsers, FiZap, FiSettings, FiAlertCircle, FiInfo, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { 
  calculateDurationHours, 
  calculateBookingCost, 
  formatDuration,
  isCarAvailable,
  combineDateAndTime,
  getBlockedHoursForDate
} from '../data/carsData';
import { Card, Button, Input, Loading } from '../components/common';
import Navbar from '../components/Navbar';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import HourlyView from '../components/HourlyView';
import { getCarById, getCarBlocks } from '../firebase/cars';
import { getBookingsByCar } from '../firebase/bookings';
import { getCompanyById } from '../firebase/companies';

const CarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [carBlocks, setCarBlocks] = useState([]);
  const [formData, setFormData] = useState({
    fromDate: '',
    fromTime: '10',
    toDate: '',
    toTime: '10'
  });
  const [errors, setErrors] = useState({});
  const [duration, setDuration] = useState(0);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [blockedHours, setBlockedHours] = useState({
    fromDate: [],
    toDate: []
  });
  const [selectingFor, setSelectingFor] = useState(null); // 'from' or 'to'
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const autoScrollRef = useRef(null);

  const carImages = car ? (car.images?.length > 0 ? car.images : car.image ? [car.image] : []) : [];

  const goToNextImage = useCallback(() => {
    if (carImages.length <= 1) return;
    setCurrentImageIndex(prev => (prev + 1) % carImages.length);
  }, [carImages.length]);

  const goToPrevImage = useCallback(() => {
    if (carImages.length <= 1) return;
    setCurrentImageIndex(prev => (prev - 1 + carImages.length) % carImages.length);
  }, [carImages.length]);

  useEffect(() => {
    if (carImages.length <= 1) return;
    autoScrollRef.current = setInterval(goToNextImage, 3000);
    return () => clearInterval(autoScrollRef.current);
  }, [carImages.length, goToNextImage]);

  const handleManualNav = (direction) => {
    clearInterval(autoScrollRef.current);
    direction === 'next' ? goToNextImage() : goToPrevImage();
    autoScrollRef.current = setInterval(goToNextImage, 3000);
  };

  const handleDotClick = (index) => {
    clearInterval(autoScrollRef.current);
    setCurrentImageIndex(index);
    autoScrollRef.current = setInterval(goToNextImage, 3000);
  };

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Restore form data from sessionStorage if available
    const savedFormData = sessionStorage.getItem('bookingFormData');
    if (savedFormData) {
      try {
        const parsed = JSON.parse(savedFormData);
        // Only restore if it's for the same car
        if (parsed.carId === id) {
          setFormData({
            fromDate: parsed.fromDate || '',
            fromTime: parsed.fromTime || '10',
            toDate: parsed.toDate || '',
            toTime: parsed.toTime || '10'
          });
        }
        // Clear the stored data after restoring
        sessionStorage.removeItem('bookingFormData');
      } catch (error) {
        console.error('Error restoring form data:', error);
      }
    }

    // Load car, company, bookings, and blocks from Firebase
    const loadCar = async () => {
      try {
        const [carResult, bookingsResult, blocksResult] = await Promise.all([
          getCarById(id),
          getBookingsByCar(id),
          getCarBlocks(id)
        ]);
        
        if (carResult.car) {
          setCar(carResult.car);
          
          // Load company pricing configuration
          if (carResult.car.companyId) {
            const companyResult = await getCompanyById(carResult.car.companyId);
            if (companyResult.company) {
              setCompany(companyResult.company);
            }
          }
        }
        
        let activeBookings = [];
        if (bookingsResult.bookings) {
          // Filter only confirmed bookings for calendar blocking (pending bookings don't block)
          activeBookings = bookingsResult.bookings.filter(
            b => b.status === 'confirmed'
          );
          setBookings(activeBookings);
        }
        
        if (blocksResult.blocks) {
          setCarBlocks(blocksResult.blocks);
        }
      } catch (error) {
        console.error('Error loading car:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCar();
  }, [id]);

  // Update duration and cost when form data changes
  useEffect(() => {
    if (car && formData.fromDate && formData.fromTime && formData.toDate && formData.toTime) {
      const fromDateTime = combineDateAndTime(formData.fromDate, formData.fromTime);
      const toDateTime = combineDateAndTime(formData.toDate, formData.toTime);
      
      const hours = calculateDurationHours(fromDateTime, toDateTime);
      setDuration(hours);
      
      if (hours >= 6) {
        const breakdown = calculateBookingCost(hours, car.price12Hour, car.price24Hour);
        setCostBreakdown(breakdown);
      } else {
        setCostBreakdown(null);
      }
    } else {
      setDuration(0);
      setCostBreakdown(null);
    }
  }, [formData, car]);

  // Update blocked hours when dates, bookings, or blocks change
  useEffect(() => {
    if (car && formData.fromDate && bookings.length >= 0) {
      const blocked = getBlockedHoursForDate(car.id, formData.fromDate, bookings, carBlocks);
      setBlockedHours(prev => ({ ...prev, fromDate: blocked }));
    }
  }, [car, formData.fromDate, bookings, carBlocks]);

  useEffect(() => {
    if (car && formData.toDate && bookings.length >= 0) {
      const blocked = getBlockedHoursForDate(car.id, formData.toDate, bookings, carBlocks);
      setBlockedHours(prev => ({ ...prev, toDate: blocked }));
    }
  }, [car, formData.toDate, bookings, carBlocks]);

  const handleFromDateChange = (value) => {
    setFormData(prev => ({ ...prev, fromDate: value }));
    setErrors(prev => ({ ...prev, fromDate: '', general: '' }));
  };

  const handleFromTimeChange = (value) => {
    setFormData(prev => ({ ...prev, fromTime: value }));
    setErrors(prev => ({ ...prev, fromTime: '', general: '' }));
  };

  const handleToDateChange = (value) => {
    setFormData(prev => ({ ...prev, toDate: value }));
    setErrors(prev => ({ ...prev, toDate: '', general: '' }));
  };

  const handleToTimeChange = (value) => {
    setFormData(prev => ({ ...prev, toTime: value }));
    setErrors(prev => ({ ...prev, toTime: '', general: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fromDate) {
      newErrors.fromDate = 'From date is required';
    }
    if (!formData.toDate) {
      newErrors.toDate = 'To date is required';
    }
    
    if (formData.fromDate && formData.toDate) {
      const fromDateTime = combineDateAndTime(formData.fromDate, formData.fromTime);
      const toDateTime = combineDateAndTime(formData.toDate, formData.toTime);
      
      const hours = calculateDurationHours(fromDateTime, toDateTime);
      
      if (hours < 6) {
        newErrors.general = 'Minimum booking duration is 6 hours';
      }
      
      // Check if dates are in the past
      const now = new Date();
      const from = new Date(fromDateTime);
      if (from < now) {
        newErrors.fromDate = 'Start date/time must be in the future';
      }
      
      // Check if toDateTime is after fromDateTime
      if (new Date(toDateTime) <= new Date(fromDateTime)) {
        newErrors.toDate = 'End date/time must be after start date/time';
      }
      
      // Check availability
      if (hours >= 6 && !isCarAvailable(car.id, fromDateTime, toDateTime, null, bookings, carBlocks)) {
        newErrors.general = 'This time range conflicts with an existing booking. Please choose different dates/times.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceedToPayment = () => {
    // Check if user is logged in
    if (!user) {
      // Store form data in sessionStorage before redirecting
      sessionStorage.setItem('bookingFormData', JSON.stringify({
        carId: id,
        fromDate: formData.fromDate,
        fromTime: formData.fromTime,
        toDate: formData.toDate,
        toTime: formData.toTime
      }));
      // Redirect to login page with return path
      navigate('/login', { state: { from: `/car/${id}` } });
      return;
    }

    if (validateForm()) {
      const fromDateTime = combineDateAndTime(formData.fromDate, formData.fromTime);
      const toDateTime = combineDateAndTime(formData.toDate, formData.toTime);
      
      const bookingData = {
        car,
        company: company ? {
          serviceChargePercent: company.serviceChargePercent || 0,
          serviceChargeEnabled: company.serviceChargeEnabled !== false,
          gstPercent: company.gstPercent || 0,
          gstEnabled: company.gstEnabled !== false
        } : null,
        fromDateTime,
        toDateTime,
        duration: formatDuration(duration),
        durationHours: duration,
        costBreakdown,
        totalAmount: costBreakdown.subtotal
      };
      navigate('/payment', { state: bookingData });
    }
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today.toISOString().split('T')[0];
  };

  const getMinToDate = () => {
    if (formData.fromDate) {
      return formData.fromDate;
    }
    return getMinDate();
  };

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: String(i).padStart(2, '0'),
    label: `${String(i).padStart(2, '0')}:00`
  }));

  // Check if an hour is blocked
  const isHourBlocked = (hour, dateType) => {
    const blocked = dateType === 'from' ? blockedHours.fromDate : blockedHours.toDate;
    const hourNum = parseInt(hour, 10);
    
    return blocked.some(range => hourNum >= range.from && hourNum < range.to);
  };

  if (loading) {
    return <Loading fullScreen text="Loading car details..." />;
  }

  if (!car) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[80vh]">
          <Card className="p-8 text-center max-w-md">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Car Not Found</h2>
            <Button onClick={() => navigate('/')} className="gradient-primary text-white">
              Go Back Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Car Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Car Image */}
            <Card className="overflow-hidden border-0 shadow-soft p-0">
              {carImages.length > 0 ? (
                <div className="relative group">
                  <div className="overflow-hidden bg-gray-900">
                    <img
                      src={carImages[currentImageIndex]}
                      alt={`${car.name} - ${currentImageIndex + 1}`}
                      className="w-full h-48 sm:h-64 md:h-96 object-contain transition-opacity duration-500"
                    />
                  </div>
                  {carImages.length > 1 && (
                    <>
                      <button
                        onClick={() => handleManualNav('prev')}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiChevronLeft size={24} />
                      </button>
                      <button
                        onClick={() => handleManualNav('next')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FiChevronRight size={24} />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {carImages.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleDotClick(idx)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                              idx === currentImageIndex
                                ? 'bg-white scale-110 shadow-md'
                                : 'bg-white/50 hover:bg-white/75'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                        {currentImageIndex + 1} / {carImages.length}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full h-48 sm:h-64 md:h-96 bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400 text-lg">No image available</span>
                </div>
              )}
            </Card>

            {/* Car Details */}
            <Card className="p-4 sm:p-6 lg:p-8 border-0 shadow-soft">
              <div className="mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
                  <div className="flex-1">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">{car.name}</h1>
                    <p className="text-base sm:text-lg text-gray-600">{car.brand} • {car.model}</p>
                  </div>
                  {(car.rating !== undefined && car.rating !== null && Number(car.rating) > 0) && (
                    <div className="flex items-center gap-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg shadow-md border border-yellow-300">
                      <FiStar className="fill-white text-white w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="font-bold text-white text-sm sm:text-base">{Number(car.rating).toFixed(1)}</span>
                      {car.totalReviews !== undefined && car.totalReviews !== null && Number(car.totalReviews) > 0 && (
                        <span className="text-yellow-100 text-xs sm:text-sm">({car.totalReviews})</span>
                      )}
                    </div>
                  )}
                </div>

                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6 whitespace-pre-wrap">{car.description}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <FiUsers className="w-6 h-6 text-primary-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-1">Seats</p>
                    <p className="font-bold text-gray-900">{car.seats}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <FiSettings className="w-6 h-6 text-primary-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-1">Transmission</p>
                    <p className="font-bold text-gray-900">{car.transmission}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <FiZap className="w-6 h-6 text-primary-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 mb-1">Fuel Type</p>
                    <p className="font-bold text-gray-900">{car.fuelType}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <svg className="w-6 h-6 text-primary-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <p className="text-sm text-gray-500 mb-1">Mileage</p>
                    <p className="font-bold text-gray-900">{car.mileage}</p>
                  </div>
                </div>

                {/* Pricing Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <FiInfo className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-900 mb-2">Rental Rates</p>
                      <div className="space-y-1 text-sm text-blue-800">
                        <p>• 12-hour slot: <span className="font-semibold">₹{car.price12Hour.toLocaleString()}</span></p>
                        <p>• 24-hour slot: <span className="font-semibold">₹{car.price24Hour.toLocaleString()}</span></p>
                        <p className="text-xs text-blue-600 mt-2">Minimum booking: 6 hours</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {car.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        <FiCheck className="w-4 h-4" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Availability Calendar */}
            <Card className="p-4 sm:p-6 lg:p-8 border-0 shadow-soft">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <FiCalendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Check Availability</h2>
              </div>
              <AvailabilityCalendar
                bookings={bookings}
                blocks={carBlocks}
                carId={car.id}
                selectedDate={selectingFor === 'from' ? formData.fromDate : formData.toDate}
                onDateSelect={(date) => {
                  if (selectingFor === 'from') {
                    handleFromDateChange(date);
                  } else if (selectingFor === 'to') {
                    handleToDateChange(date);
                  } else {
                    // Default to from date if nothing selected
                    handleFromDateChange(date);
                  }
                }}
                minDate={getMinDate()}
              />
            </Card>

            {/* Hourly View */}
            {(formData.fromDate || formData.toDate) && (
              <HourlyView
                bookings={bookings}
                blocks={carBlocks}
                carId={car.id}
                date={selectingFor === 'from' ? formData.fromDate : formData.toDate || formData.fromDate}
              />
            )}
          </div>

          {/* Right Column - Booking Form */}
          <div className="lg:col-span-1">
            <Card className="p-4 sm:p-6 sticky top-20 sm:top-24 border-0 shadow-medium">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Book This Car</h2>

              <div className="space-y-4">
                {/* From Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiCalendar className="inline w-4 h-4 mr-1" />
                    From Date
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                    onFocus={() => setSelectingFor('from')}
                    min={getMinDate()}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                      errors.fromDate ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  {errors.fromDate && (
                    <p className="text-red-500 text-xs mt-1">{errors.fromDate}</p>
                  )}
                </div>

                {/* From Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiClock className="inline w-4 h-4 mr-1" />
                    From Time
                  </label>
                  <select
                    value={formData.fromTime}
                    onChange={(e) => handleFromTimeChange(e.target.value)}
                    onFocus={() => setSelectingFor('from')}
                    disabled={!formData.fromDate}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {hourOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        disabled={isHourBlocked(option.value, 'from')}
                      >
                        {option.label} {isHourBlocked(option.value, 'from') ? '(Booked)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* To Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiCalendar className="inline w-4 h-4 mr-1" />
                    To Date
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleToDateChange(e.target.value)}
                    onFocus={() => setSelectingFor('to')}
                    min={getMinToDate()}
                    disabled={!formData.fromDate}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                      errors.toDate ? 'border-red-500' : 'border-gray-200'
                    }`}
                  />
                  {errors.toDate && (
                    <p className="text-red-500 text-xs mt-1">{errors.toDate}</p>
                  )}
                </div>

                {/* To Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FiClock className="inline w-4 h-4 mr-1" />
                    To Time
                  </label>
                  <select
                    value={formData.toTime}
                    onChange={(e) => handleToTimeChange(e.target.value)}
                    onFocus={() => setSelectingFor('to')}
                    disabled={!formData.toDate}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {hourOptions.map(option => (
                      <option 
                        key={option.value} 
                        value={option.value}
                        disabled={isHourBlocked(option.value, 'to')}
                      >
                        {option.label} {isHourBlocked(option.value, 'to') ? '(Booked)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Duration Display */}
                {formData.fromDate && formData.toDate && duration >= 0 && (
                  <div className={`border rounded-lg p-3 ${duration >= 6 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Duration:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {duration === 0 ? '0 hours (Select different times)' : formatDuration(duration)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Duration Warning */}
                {duration >= 0 && duration < 6 && formData.fromDate && formData.toDate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <FiAlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900">
                          {duration === 0 ? 'Invalid Time Selection' : 'Minimum Duration Not Met'}
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          {duration === 0 
                            ? 'Please select a different To Time or To Date. From and To cannot be the same.'
                            : `You need at least ${6 - duration} more hour(s). Minimum booking is 6 hours.`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* General Error */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                      <p className="text-sm text-red-800">{errors.general}</p>
                    </div>
                  </div>
                )}

                {/* Cost Breakdown */}
                {costBreakdown && duration >= 6 && (
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-5 rounded-xl border border-primary-200">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Duration:</span>
                        <span className="font-semibold text-gray-900">{formatDuration(duration)}</span>
                      </div>
                      
                      {costBreakdown.slots24Hour > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{costBreakdown.slots24Hour} × 24hr slots:</span>
                          <span className="font-semibold text-gray-900">₹{costBreakdown.cost24Hour.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {costBreakdown.slots12Hour > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{costBreakdown.slots12Hour} × 12hr slots:</span>
                          <span className="font-semibold text-gray-900">₹{costBreakdown.cost12Hour.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-primary-200 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900">Total:</span>
                        <span className="text-2xl font-bold text-primary-600">
                          ₹{costBreakdown.subtotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sign In Required Message */}
                {!user && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <FiAlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-900">Sign In Required</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Please sign in to make a reservation
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  fullWidth
                  size="lg"
                  onClick={handleProceedToPayment}
                  disabled={!formData.fromDate || !formData.toDate || duration < 6}
                  className="gradient-primary text-white shadow-soft mt-6"
                >
                  {!user ? 'Sign In to Book' : 'Proceed to Payment'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetails;
